// libraries
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { get, startCase, lowerCase, sortBy, get as _get } from 'lodash';

// store and utils
import * as actions from '../../../store/actions';
import * as ODCalls from '../../../store/calls/indicativeOptionData';
import validators from '../../../utils/forms/validators';
import formatters from '../../../utils/forms/formatters';
import api from '../../../store/api';
import { isUserLoggedIn } from '../../../utils/session/logout';

// components
import FormFlowWrapper from '../../theme/wrappers/FormFlowWrapper';
import FormFlowStepForm from '../../theme/forms/FormFlowStepForm';
import BaseForm from '../../theme/forms/BaseForm';
import Input from '../../theme/forms/Input';
import ChevronSteps from '../../theme/forms/ChevronSteps';
import {
  addErrorAlert,
  toggleGlobalLoader,
} from '../../../store/calls/alertLoadingDispatch';
import SelectByCategory from '../../theme/forms/SelectByCategory';
import InfoFeed from '../../theme/cardlists/infoFeed';
import FormFlow from '../../theme/formFlows/FormFlow';
import TabsSteps from '../../theme/forms/TabsSteps';
import { updateOrCreateProjectLead } from '../../../store/calls/indicativeOptionData';
import StaticCornerMap from '../../theme/maps/staticCornerMap';
import AlertCard from '../../theme/cards/AlertCard';
import { getSubdomain } from '../../../utils/general';
import TelephoneNumber from '../../subcomponents/TelephoneNumber';
import ContactEmail from '../../subcomponents/ContactEmail';
import ConfirmModal from "../../theme/modals/ConfirmModal";
import LoginModal from "../../subcomponents/auth/LoginModal";
import StaticMapboxImage from "../../theme/maps/staticMap";
import TaxInputForm from "../../subcomponents/admin/TaxInputForm";


class IndicativeFormFlow extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      inputs: [],
      errors: {},
      currTab: 4,
      multiScopeConfirmed: false,
      showMultiScopeConfirmModal: false,
      showLoginModal: false,

      // Track original defaults from the form to restore later if we auto-set.
      defaultBusinessType: props?.inputs?.business_type ?? null,
      defaultAssetType: props?.inputs?.asset_type ?? null,

      // Flags to know if we auto-set the values due to Tesla-only selection.
      autoSetBusinessType: false,
      autoSetAssetType: false,
    };
  }

  // Identify Tesla Roof / Powerwall improvements by name/category (swap for ID if you prefer).
  isTeslaImpById = (id) => {
    const { optionData } = this.props;
    const imp = (optionData?.improvements || []).find(i => i.id === id);
    if (!imp) return false;
    const name = (imp.name || '');
    const cat  = (imp.category || '');
    return (
      /tesla\s*roof/i.test(name) ||
      /powerwall/i.test(name)
    ) && /residential\s*high\s*value/i.test(cat);
  };

  currentSelections = () => (this.props.inputs?.improvements || []).map(d => d.id);

  selectionState = () => {
    const selectedIds = this.currentSelections();
    const hasTesla    = selectedIds.some(this.isTeslaImpById);
    const hasNonTesla = selectedIds.some(id => !this.isTeslaImpById(id));
    return { selectedIds, hasTesla, hasNonTesla };
  };

  // Used to hide the tax step when Tesla-only is selected
  isTeslaRoofSelected = () => {
    const { optionData, inputs } = this.props;
    const selectedIds = (inputs?.improvements || []).map(d => d.id);
    const imps = optionData?.improvements || [];
    return imps.some(imp =>
      selectedIds.includes(imp.id) &&
      (
        /tesla\s*roof/i.test(imp.name || '') ||
        /powerwall/i.test(imp.name || '')
      ) &&
      /residential\s*high\s*value/i.test(imp.category || '')
    );
  };

  /**
   * If only Tesla is selected:
   *  - Auto-set business_type = "Individual - Non-business"
   *  - Auto-set asset_type = "Residential"
   * If Tesla is no longer selected and we previously auto-set:
   *  - Restore both to the original defaults from the form.
   */
  maybeAutoSelectResidentialIndividual = () => {
    const { optionData, inputs } = this.props;
    const { hasTesla, hasNonTesla } = this.selectionState();
    const onlyTesla = hasTesla && !hasNonTesla;

    // Resolve references:
    const individual = (optionData?.businessTypes || []).find(b => /^Individual - Non-business$/i.test(b.name || ''));
    const residential = (optionData?.assetTypes || []).find(a => /^residential$/i.test(a.name || ''));

    // 1) Only Tesla selected -> auto-set both fields
    if (onlyTesla) {
      // business_type
      if (individual && inputs.business_type !== +individual.id) {
        this.props.updateInputs({ business_type: +individual.id });
        this.updateProjectInDB({ business_type: +individual.id });
      }
      if (!this.state.autoSetBusinessType) {
        this.setState({ autoSetBusinessType: true });
      }

      // asset_type
      if (residential && inputs.asset_type !== +residential.id) {
        this.props.updateInputs({ asset_type: +residential.id });
        this.updateProjectInDB({ asset_type: +residential.id });
      }
      if (!this.state.autoSetAssetType) {
        this.setState({ autoSetAssetType: true });
      }
      return;
    }

    // 2) Tesla no longer selected -> restore defaults if we auto-set earlier
    if (!hasTesla) {
      // business_type
      if (this.state.autoSetBusinessType) {
        const defaultBT = this.state.defaultBusinessType ?? null;
        if (inputs.business_type !== defaultBT) {
          this.props.updateInputs({ business_type: defaultBT });
          this.updateProjectInDB({ business_type: defaultBT });
        }
        this.setState({ autoSetBusinessType: false });
      }

      // asset_type
      if (this.state.autoSetAssetType) {
        const defaultAT = this.state.defaultAssetType ?? null;
        if (inputs.asset_type !== defaultAT) {
          this.props.updateInputs({ asset_type: defaultAT });
          this.updateProjectInDB({ asset_type: defaultAT });
        }
        this.setState({ autoSetAssetType: false });
      }
    }
  };

  componentDidUpdate(prevProps) {
    const prevImps = (prevProps.inputs?.improvements || []).map(d => d.id).join(',');
    const currImps = (this.props.inputs?.improvements || []).map(d => d.id).join(',');
    if (prevImps !== currImps) {
      this.maybeAutoSelectResidentialIndividual();
    }
  }

  componentDidMount() {
    // Persist initial defaults if they arrive after mount
    const patches = {};
    if (this.state.defaultBusinessType === null && this.props?.inputs?.business_type != null) {
      patches.defaultBusinessType = this.props.inputs.business_type;
    }
    if (this.state.defaultAssetType === null && this.props?.inputs?.asset_type != null) {
      patches.defaultAssetType = this.props.inputs.asset_type;
    }
    if (Object.keys(patches).length) {
      this.setState(patches);
    }

    toggleGlobalLoader(true);
    Promise.allSettled([
      ODCalls.getAssetTypes(),
      ODCalls.getBusinessTypes(),
      ODCalls.getAvailableImprovements(),
    ])
      .catch(() => {
        addErrorAlert('Failed to load all initial data.', 10 * 1000);
      })
      .finally(() => {
        toggleGlobalLoader(false);
      });

    // for debugging particular steps
    // this.props.setCurrStep(5);
  }

  setCurrentTab = (tab) => {
    this.setState({ ...this.state, currTab: tab });
  };

  getBusinessTypes = () => {
    api
      .get('/business-types/')
      .then((res) => this.setState({ businessTypes: res.data }));
  };

  getAssetClasses = () => {
    api
      .get('/asset-types/')
      .then((res) => this.setState({ assetTypes: res.data }));
  };

  getFormFlowConfig() {
	const hideTaxStep = this.isTeslaRoofSelected();
  
	const steps = [
	  {
		title: 'What improvements are you making?',
		onComplete: () => {
		  if (!this.props.focusedProject) {
			this.createProjectInDB();
		  } else {
			this.updateProjectInDB();
		  }
		},
		component: this.renderSelectImprovementStep('What improvement(s) are you making?'),
		areStepReqsMet: () => !!get(this.props, 'inputs.improvements', []).length,
	  },
	  {
		title: 'How much capital do you need for these improvements?',
		onComplete: () => {
		  this.updateProjectInDB();
		},
		inputs: this.getImprovementValueInputs(),
		areStepReqsMet: () =>
		  !!get(this.props, 'inputs.improvements', []).every((d) => d.value && !isNaN(d.value)),
	  },
	  {
		title: 'What kind of asset is this?',
		onComplete: () => {
		  this.updateProjectInDB();
		},
		inputs: [
		  {
			type: 'select',
			dataKey: 'business_type',
			label: 'Business Type',
			inputClasses: 'is-medium is-fullwidth',
			controlClasses: 'is-expanded',
			placeholder: '--',
			options: this.props.optionData.businessTypes.map((d) => ({
			  value: +d.id,
			  display: d.name,
			})),
			...validators.exists,
		  },
		  {
			type: 'select',
			dataKey: 'asset_type',
			label: 'Asset Type',
			inputClasses: 'is-medium is-fullwidth',
			controlClasses: 'is-expanded',
			placeholder: '--',
			options: sortBy(
			  this.props.optionData.assetTypes.map((d) => ({
				value: d.id,
				display: d.name,
			  })),
			  'display'
			),
			...validators.exists,
		  },
		  {
			type: 'select',
			dataKey: 'ownership_status',
			label: 'Is the property owned or leased?',
			inputClasses: 'is-medium is-fullwidth',
			controlClasses: 'is-expanded',
			placeholder: '--',
			options: [
			  { value: 'own', display: 'Owned' },
			  { value: 'lease', display: 'Leased' },
			],
			...validators.exists,
		  },
		],
	  },
	  {
		title: 'Some information about your project',
		onComplete: () => {
		  this.updateProjectInDB();
		},
		inputs: [...this.getStep4Inputs()].map((d) => ({
		  ...this.updateInputWithDataKey(d.dataKey),
		  ...d,
		})),
	  },
	  {
		title: 'Give your project a name',
		onComplete: () => {
		  this.updateProjectInDB();
		},
		inputs: [
		  {
			type: 'text',
			dataKey: 'project_name',
			label: 'Project Name',
			inputClasses: 'is-medium',
			...validators.isStringOfMinLength(2),
		  },
		],
	  },
	];
  
	// Step 6: Only added if is not teslaroof
	if (!hideTaxStep) {
	  steps.push({
		title: 'This project has tax benefits.',
		onComplete: () => {
		  this.updateProjectInDB();
		},
		component: (
		  <TaxInputForm
			onNoTax={this.onFormFlowComplete}
		  />
		),
	  });
	}
  
	return {
	  title: 'Get Indicative',
	  onComplete: this.onFormFlowComplete,
	  steps,
	};
  }

  // returns spreadable dict to update the value of inputs in the form flow
  updateInputWithDataKey = (dataKey) => {
    return {
      onChange: (val) => this.props.updateInputs({ [dataKey]: val }),
    };
  };

  updateProjectInDB = (extraData) => {
    let data = {
      data: {
        ...this.props.computed,
        ...this.props.inputs,
        id: this.props.focusedProject,
        ...extraData
      },
    };

    return updateOrCreateProjectLead(data);
  };

  createProjectInDB = () => {
    const subdomain = getSubdomain();
    let data = {
      user: this.props.userData.user_id,
      pace_available: true,
      subdomain,
      data: {
        ...this.props.computed,
        ...this.props.inputs,
        id: this.props.focusedProject,
        subdomain: getSubdomain(),
      },
    };

    return updateOrCreateProjectLead(data).then((res) => {
      this.props.setFocusedProject(res.data.id);
      return res;
    });
  };

  // Selection logic enforcing Tesla exclusivity and disabling other checkboxes accordingly.
  onSelectImprovement = (selected, id) => {
    const { selectedIds } = this.selectionState();
    const isTesla = this.isTeslaImpById(id);
    let next = [...selectedIds];

    if (selected) {
      if (isTesla) {
        // If Tesla is selected: keep ONLY Tesla selected.
        next = [id];
      } else {
        // If non-Tesla is selected: remove Tesla if present, allow multiple non-Tesla.
        next = next.filter(x => this.isTeslaImpById(x) === false);
        if (!next.includes(id)) next.push(id);
      }
    } else {
      // Unselect: simply remove the id. Rendering will re-enable others if needed.
      next = next.filter(x => x !== id);
    }

    const improvements = next.map(x => ({ id: x }));
    this.props.updateInputs({ improvements });
  };

  renderSelectImprovementStep(title) {
    const multipleCategoriesSelected =
      [
        ...new Set(
          (this.props.inputs?.improvements || [])
            .map((d) =>
              (this.props.optionData.improvements || []).find(
                (imp) => imp.id === d.id
              )
            )
            .filter((d) => d)
            .map((d) => d.category)
        ),
      ].length > 1;

    let improvementsData = sortBy(
      this.props.optionData.improvements || [],
      'category'
    );

    // Compute per-item disabled state based on current selection
    const { selectedIds, hasTesla, hasNonTesla } = this.selectionState();
    const onlyTeslaSelected = hasTesla && !hasNonTesla;

    const mappedData = improvementsData.map((d) => {
      const isSelected = selectedIds.includes(d.id);
      const isTesla    = this.isTeslaImpById(d.id);

      // Disable rules:
      // - If ONLY Tesla is selected => disable all non-Tesla items.
      // - If any non-Tesla is selected => disable Tesla.
      // - If nothing selected => nothing disabled.
      let disabled = false;
      if (onlyTeslaSelected && !isTesla) {
        disabled = true;
      } else if (hasNonTesla && isTesla) {
        disabled = true;
      }

      return isSelected ? { ...d, selected: true, disabled } : { ...d, disabled };
    });

    return (
      <>
        {/* <div className={"form-section-header"}>{title}</div> */}
        {/* this horrendous piece of code asks: is there more than one category selected? */}
        {multipleCategoriesSelected && (
          <div className="mb-4">
            <AlertCard
              type="warning"
              msg={`If you have a multi-scope project, you will receive the greatest number of 
                  funding proposals by separating your scopes into different projects.`}
            />
          </div>
        )}
        <SelectByCategory
          // IMPORTANT: we pass `disabled` per item. Ensure SelectByCategory uses it to disable its underlying checkboxes/controls.
          data={mappedData}
          onOptionValueChange={this.onSelectImprovement}
          topLevelLeftHTML={
            <div className="level-item">
              <div
                className="button is-primary"
                onClick={() => {
                  this.onSelectImprovement(true, 32);
                  if (!this.props.focusedProject) {
                    this.createProjectInDB();
                  } else {
                    this.updateProjectInDB();
                  }
                  this.props.setCurrStep(this.props.currStep + 1);
                  this.sendGtagNextStepEvent && this.sendGtagNextStepEvent();
                }}
              >
                <i className="fas fa-sun mr-3" />
                I'm only looking for solar!
              </div>
            </div>
          }
          startExpanded={false}
        />

        {multipleCategoriesSelected && (
          <div className="mt-4">
            <AlertCard
              type="warning"
              msg={`If you have a multi-scope project, you will receive the greatest number of 
                  funding proposals by separating your scopes into different projects.`}
            />
          </div>
        )}
      </>
    );
  }

  // price the value of each improvement
  getImprovementValueInputs() {
    let improvements = get(this.props, 'inputs.improvements', []);
    let inputs = improvements.map((d) => {
      return {
        type: 'text',
        dataKey: `improvement_val_${d.id}`,
        label: `Estimated value of ${
          (
            this.props.optionData.improvements.find((imp) => imp.id === d.id) ||
            {}
          ).name
        }`,
        inputClasses: 'is-medium',
        ...validators.isNumberNotNull,
        ...formatters.USDCurrency,
        onChange: (val) => {
          let idx = improvements.findIndex((imp) => imp.id === d.id);
          val = val.replace(/[^0-9.]/g, '')
          improvements[idx].value = val;
          this.props.updateInputs({ improvements });
        },
        value: (improvements.find((imp) => imp.id === d.id) || {}).value,
      };
    });

    return inputs;
  }

  getStep4Inputs = () => {
    let improvements = _get(this.props, 'inputs.improvements', []).map(
      (d) => d.id
    );
    let project_includes_solar = improvements.includes(32); // improvement includes solar

    let inputs = [
      {
        type: 'date',
        dataKey: 'start_date',
        label: 'Approx Project Start Date',
        subLabel: 'Used to calculate capitalized interest',
        inputClasses: 'is-medium',
        validator: (val) => val,
      },
    ];

    if (project_includes_solar) {
      inputs.push({
        type: 'text',
        dataKey: 'solar_system_size',
        label: 'How large is the solar system in kW DC?',
        subLabel:
          "<b>You can leave this blank if you aren't sure, but some financing products may not be quoted.</b>",
        validator: (val) => [null, undefined, ''].includes(val) || (!isNaN(parseInt(val)) && parseInt(val) <= 5000 && parseInt(val) >= 10),
        ...formatters.number,
        inputClasses: 'is-medium',
        errorText: 'The system size must be a number greater than 10 and less than 5,000. For larger projects than that, please call us directly.',
      });
    }

    return inputs;
  };

  splitScopes = () => {
    /*
     * This function examines the scopes of the different projects to ensure that they can be included in one project
     * Improvements with tax benefits each have their own scope. Improvements without tax benefits are lumped into the same project.
     *
     * @returns: an array of arrays, containing the selected improvements, assigned by project
     * */

    let {
      optionData: { improvements: improvementsData },
      inputs: { improvements: inputImprovements },
    } = this.props;

    // score separated scoped into different projects;
    // no tax improvements, or only one project, goes to zero index.
    // every scope with an improvement goes to a new index;
    let scopes = [[], []];
    inputImprovements = (inputImprovements || []).filter(d => d.value);
    inputImprovements.forEach((d) => {
      let imp = {
        ...(improvementsData.find((_d) => _d.id === d.id) || {}),
        ...d,
      };
      if (!imp.tax_benefit_fixed_subsidy && !imp.tax_benefit_percentage && !imp.direct_pay_percentage) {
        scopes[0].push(imp);
      } else {
        scopes[1].push([imp]);
      }
    });

    // remove empty scopes
    scopes = scopes.filter(d => d.length)

    return scopes;
  }

  doFinalUpdateOnSplitScopes = () => {
    const scopes = this.splitScopes();

    if (scopes.length === 1) {
      return this.updateProjectInDB().then(res => [res.data]);
    } 
    let promises = [];
    scopes.forEach((p, i) => {
      let data = {
        data: {
          ...this.props.computed,
          ...this.props.inputs,
          id: i === 0 ? this.props.focusedProject : null,
          improvements: p,
          project_name: `${this.props.inputs?.project_name} (${p.map(
            (imp, _i) => `${imp.name}${_i !== p.length - 1 ? ', ' : ''}`
          )})`,
        },
        user: this.props.userData.user_id,
      };

      promises.push(updateOrCreateProjectLead(data).then(res => res.data));
    });

    return Promise.all(promises);
  };

  onFormFlowComplete = () => {
    let splitScopeRequired = this.splitScopes().length > 1;
    if (splitScopeRequired && !this.state.multiScopeConfirmed) {
      this.setState({ showMultiScopeConfirmModal: true})
      return;
    }

    let userIsLoggedIn = isUserLoggedIn();

    this.doFinalUpdateOnSplitScopes()
      .then((res) => {
        let pushQueue = [...res];
        let tabsToOpen = []
        while (pushQueue.length > 1) {
          let projToOpen = pushQueue.pop();
          let url = `/project-estimate/${projToOpen.id}`;
          tabsToOpen.push({url, tabName: projToOpen.id});
        }

        let currentTabRedirectPath = '/project-estimate/' + pushQueue[0].id

        if (userIsLoggedIn) {
          tabsToOpen.forEach(d => window.open(d.url, d.tabName))
          this.props.history.push(currentTabRedirectPath);
        } else {
          this.props.setSessionVar({tabsToOpenOnCompletion: tabsToOpen});
          this.props.setSessionVar({onLoginPath: currentTabRedirectPath});
          this.setState({showLoginModal: true})
        }
      });
  };

  renderMultiScopeConfirmModal = () => {
    let scopes = this.splitScopes();

    let scopedDisplay = scopes.map((d,i) => (
      <p key={i}>Scope {i + 1}: {d.map((imp, _i) => `${imp.name}${_i !== d.length - 1 ? ', ' : ''}`)}</p>
    ))

    return (
      <ConfirmModal
        visible={this.state.showMultiScopeConfirmModal}
        onAccept={() => {
          this.setState({
            multiScopeConfirmed: true,
            showMultiScopeConfirmModal: false
          }, this.onFormFlowComplete)
        }}
        onCancel={() => this.setState({ showMultiScopeConfirmModal: false })}
        prompt="This project must be split into multiple scopes."
      >
        <div>
          {scopedDisplay}
          <p className="mt-4 mb-6 has-text-weight-semibold">
            Additional scopes will open in new tabs. Please allow popups
            in your browser to view all your project options.
          </p>
        </div>
      </ConfirmModal>
    )
  }

  renderStepsAndTitle() {
	const hideTaxStep = this.isTeslaRoofSelected();
	const totalSteps = hideTaxStep ? 5 : 6;
  
	return (
	  <>
		{/** DESKTOP */}
		<h2 className="is-size-2 has-text-primary mb-4 only-desktop">
		  Get Your Commercial Financing Options
		</h2>
		<ChevronSteps stepCount={totalSteps} activeStep={this.props.currStep} />
	  </>
	);
  }

  renderSideCards() {
    return (
      <div className="info-feed is-hidden-touch">
        <div className="card mb-3">
          <div className="card-content">
            <div className="card-header-title is-size-5 has-text-primary pl-0">
              Project Address
            </div>
            <p className="my-2">{this.props.computed.address?.formatted_address}</p>
            <StaticMapboxImage latitude={this.props.computed.address?.lat} longitude={this.props.computed.address?.lng} />
            <div className="my-2 button is-text pl-0" onClick={() => this.props.history.push('/')}>Look up another address</div>
          </div>
        </div>
        <div className="card mb-3">
          <div className="card-content">
            <div className="card-header-title is-size-5 has-text-primary pl-0">
              Got Questions ? Need help?
            </div>
            <p>
              <TelephoneNumber className="is-size-3 has-text-semibold" />
            </p>
            <ContactEmail
              className="is-size-6 has-text-black-bis has-text-semibold"
            />
          </div>
        </div>
        {!!(this.props.computed?.programs || []).length && (
          <div className="box">
              <div className="has-text-success is-flex">
                <span className="icon mt-1 mr-5 is-size-3">
                  <i className="fas fa-check-circle" />
                </span>
                <span className="is-size-5 has-text-weight-semibold">
                  This property is pace eligible!
                </span>
              </div>
            </div>
        )}
      </div>
    );
  }

  renderMobileFooter() {
    return (
      <footer className="footer is-hidden-desktop">
        <div className="content has-text-centered">
          <p>
            <strong>Have Questions? Call now. </strong>
          </p>
          <p className="is-size-3">
            <TelephoneNumber />
          </p>
          <p>
            <a href="mailto:admin@cleanfi.com">admin@cleanfi.com</a>
          </p>
        </div>
      </footer>
    );
  }

  render() {
    let { lat, lng } = _get(this.props.computed, 'address', {
      lat: 37.819929,
      lng: -122.478255,
    }); // defaults to golden gate bridge

    return (
      <FormFlowWrapper>
        {this.renderStepsAndTitle()}
        <div className="columns">
          <div className="column is-three-fifths">
            <FormFlow {...this.getFormFlowConfig()} />
          </div>

          <div className="column">{this.renderSideCards()}</div>
        </div>
        {this.renderMobileFooter()}
        {this.renderMultiScopeConfirmModal()}
        <LoginModal
          visible={this.state.showLoginModal}
          onLogin={(userData) => {
            api.patch(`/project-leads/${this.props.focusedProject}/`, {user: userData.user_id})
          }}
        />
      </FormFlowWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  inputs: state.formFlow.inputs,
  computed: state.formFlow.computed,
  optionData: state.indicativeOptionData,
  currStep: state.formFlow.currStep,
  infoFeedDisplay: state.infoFeed,
  focusedProject: state.projectLeads.focusedProject,
  userData: state.user.decodedTokenData,
});

const mapDispatchToProps = (dispatch) => ({
  updateInfoFeedDisplay: (data) =>
    dispatch(actions.infoFeed.updateInfoFeedDisplay(data)),
  updateInputs: (data) => dispatch(actions.formFlow.updateFormFlowInputs(data)),
  setFocusedProject: (data) =>
    dispatch(actions.projectLeads.setFocusedProject(data)),
  setCurrStep: (data) => dispatch(actions.formFlow.setFormFlowCurrStep(data)),
  setSessionVar: (data = {}) => dispatch(actions.session.setVar(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IndicativeFormFlow);